import phone from 'phone';
import validator from 'validator';
import zxcvbn from 'zxcvbn';

import { CurrentUser } from '../hooks/useCurrentUser';

export const makeValidator =
  <T>(isValidFn: (input: T) => boolean, errorMsg: string) =>
  (input: T): string | undefined => {
    if (!isValidFn(input)) {
      return errorMsg;
    }
  };

export const simplifyValidator =
  <T>(validatorFn: (input: T) => string | undefined) =>
  (input: T): boolean =>
    !validatorFn(input);

export const isAccountingFirmUser = (currentUser: CurrentUser): boolean =>
  !!currentUser.organization && currentUser.organization.isAccountingFirm;

export const isEin = (ein: string): boolean => /^[1-9]\d?-\d{7}$/.test(ein);

export const isPostalCode = (postalCode: string): boolean => validator.isPostalCode(postalCode, 'US');

export const isUrl = (url: string): boolean => validator.isURL(url);

export const validateEmail = (email: string): boolean => {
  if (
    !validator.isEmail(email, {
      require_tld: true,
      allow_utf8_local_part: false,
    })
  ) {
    return false;
  }

  // Additional TLD validation
  // TLD must be 2-6 characters long and only contain letters
  const tldRegex = /^[^@]+@[^@]+\.[a-zA-Z]{2,6}$/;
  return tldRegex.test(email);
};

export const isDate = (date: string): boolean => validator.isDate(date, { format: 'MM/DD/YYYY', strictMode: true });

export const isValidIncorporationDate = (date: string): boolean => {
  if (!isDate(date)) {
    return false;
  }
  const incorporationDate = new Date(date);
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  return (
    incorporationDate.getFullYear() >= 1900 &&
    incorporationDate.getFullYear() <= currentYear &&
    incorporationDate < currentDate
  );
};

export const isPositiveInteger = (value: number): boolean => Number.isInteger(value) && value >= 0;

const upToTwoDecimals = /^-?[0-9]*(\.[0-9]{0,2})?$/;
export const validateAmount = (amount?: string | number | null): string | undefined => {
  const amountAsNumber = Number(amount);
  if (amount === undefined || amount === null || amount === '' || Number.isNaN(amountAsNumber)) {
    return 'A valid number is required.';
  }
  if (typeof amount === 'string' && !upToTwoDecimals.test(amount)) {
    return 'Amount must be a valid number with no more than two decimal places.';
  }
  if (amountAsNumber < 0) {
    return 'Amount must be greater than or equal to zero.';
  }
  if (amountAsNumber >= 1_000_000_000_000) {
    return 'Amount must be less than 1 trillion.';
  }
};
export const isValidAmount = simplifyValidator(validateAmount);

const MIN_PASSWORD_LENGTH = 8;
const MIN_PASSWORD_SCORE = 3;
const validateSignUpPassword = (password: string): string | undefined => {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password is too short (>=${MIN_PASSWORD_LENGTH})`;
  }

  const { score } = zxcvbn(password);
  if (score < MIN_PASSWORD_SCORE) {
    return 'Password is easily guessable! Try adding more words or removing dictionary words and common sequences like ABC, 123, !@#.';
  }
};

export const validatePassword = async (password: string): Promise<string | undefined> => {
  if (!password) return;
  if (!password.trim()) {
    return 'Password is required';
  }
  return await validateSignUpPassword(password);
};

export const validateEmailAddress = (email: string, required = true): string | undefined => {
  if (!required && !email.trim()) {
    return;
  }
  if (required && !email.trim()) {
    return 'Email address is required.';
  }
  if (!validateEmail(email)) {
    return 'Email address is not valid.';
  }
};

export const validateFullName = (fullName: string): string | undefined => {
  if (!fullName.trim()) {
    return 'Full name is required.';
  } else if (!/^[^<>{}()[\]=?!%]+$/.test(fullName.trim())) {
    return 'Invalid character in full name.';
  }
};

export const validateIfExist = (input: string, name?: string): string | undefined => {
  if (!input.trim()) {
    if (name) return `${name} is required`;
    return 'required';
  }
};

export const validateUrl = (input: string): string | undefined => {
  if (input && !isUrl(input)) {
    return 'Invalid URL';
  }
};

export const validateUsPhoneNumber = (phoneNumber: string): string | undefined => {
  if (!phoneNumber.trim()) {
    return 'Phone number is required';
  }
  const { isValid } = phone(phoneNumber, { country: 'USA' });
  if (!isValid) {
    return 'Invalid phone number';
  }
};

/**
 * @param priorEntityDateString Value from the company formation date field
 * @param incorporationDateString Value from the incorporation date field
 * @param lastDayOfThePeriod The last day of the current company's period. Should be in format: "YYYY-MM-DD"
 */
export const validatePriorEntityDate = ({
  incorporationDateString,
  lastDayOfPeriod,
  priorEntityDateString,
  priorEntityYear,
}: {
  priorEntityDateString: string;
  priorEntityYear?: number;
  incorporationDateString?: string | null;
  lastDayOfPeriod: string;
}): string | undefined => {
  if (isNaN(Date.parse(priorEntityDateString))) {
    return 'Invalid date';
  }

  const priorEntityDate = new Date(priorEntityDateString);
  if (priorEntityYear && priorEntityDate.getUTCFullYear() !== priorEntityYear) {
    return 'Year of original entity start date should match the year of original entity start year';
  }
  if (incorporationDateString != null && priorEntityDate > new Date(incorporationDateString)) {
    return 'Original entity start date cannot be after the incorporation date';
  }
  if (priorEntityDate < new Date('1900-01-01T00:00:00.000Z')) {
    return 'Original entity start date cannot be before the year 1900';
  }
  if (priorEntityDate > new Date()) {
    return 'Original entity start date cannot be in the future';
  }

  const lastDayOfTaxYear = new Date(lastDayOfPeriod);
  if (priorEntityDate >= lastDayOfTaxYear) {
    return 'Original entity start date should be before the last day of the current tax year';
  }
};

export const validatePositiveInteger = makeValidator<number>(isPositiveInteger, 'A positive integer is required');

export const validatePostalCode = makeValidator<string>(isPostalCode, 'Invalid postal code');

export const validateEin = makeValidator<string>(isEin, 'Invalid EIN');
